import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, User, Lock, CreditCard, PencilSimple, X } from '@phosphor-icons/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { profileAPI } from '../../utils/api';
import { checkPasswordRequirements, validateNewPassword } from '../../utils/validation';
import { VOICE_MODE_ENABLED } from '../../config/features';

const VisibilityIcon = MdVisibility as any;
const VisibilityOffIcon = MdVisibilityOff as any;

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, userProfile, refreshProfile, logout } = useAuth();
  const { subscription, isTrial, isActive, daysLeft, tier, isComplimentary } = useSubscription();

  // Edit profile modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editClassType, setEditClassType] = useState<'voice' | 'text'>('text');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Change password modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Profile picture
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const passwordRequirements = useMemo(() => checkPasswordRequirements(newPassword), [newPassword]);

  const openEditModal = () => {
    setEditName(userProfile?.full_name || '');
    setEditClassType(userProfile?.preferred_class_type || 'text');
    setProfileError('');
    setShowEditModal(true);
  };

  const openPasswordModal = () => {
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      setProfileError('Name cannot be empty.');
      return;
    }
    setSavingProfile(true);
    setProfileError('');
    try {
      await profileAPI.updateProfile({ full_name: editName.trim(), preferred_class_type: editClassType });
      await refreshProfile();
      setShowEditModal(false);
    } catch {
      setProfileError('Failed to update profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    const validation = validateNewPassword(newPassword, confirmPassword);
    if (!validation.isValid) {
      setPasswordError(validation.error || 'Invalid password.');
      return;
    }
    setSavingPassword(true);
    setPasswordError('');
    try {
      await profileAPI.changePassword({ new_password: newPassword });
      setShowPasswordModal(false);
      await logout();
      navigate('/login');
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setNewPassword(val);
    if (confirmPassword) {
      const v = validateNewPassword(val, confirmPassword);
      setPasswordError(v.isValid ? '' : v.error || '');
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setConfirmPassword(val);
    if (newPassword && val) {
      const v = validateNewPassword(newPassword, val);
      setPasswordError(v.isValid ? '' : v.error || '');
    }
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPicture(true);
    try {
      await profileAPI.uploadProfilePicture(file);
      await refreshProfile();
    } catch (err) {
      console.error('Failed to upload profile picture:', err);
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const profileInitial = user?.fullName?.charAt(0)?.toUpperCase() || '?';

  const RequirementItem = ({ met, label }: { met: boolean; label: string }) => (
    <li className={`flex items-center gap-2 ${newPassword.length === 0 ? 'text-text-secondary' : met ? 'text-green-500' : 'text-red-400'}`}>
      {newPassword.length === 0 ? (
        <span className="w-4 h-4 inline-block" />
      ) : met ? (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      <span>{label}</span>
    </li>
  );

  return (
    <Layout>
      <div className="px-4 md:px-8 lg:px-40 flex flex-1 justify-center py-8">
        <div className="flex flex-col max-w-[720px] w-full gap-6">

          <h1 className="text-white text-2xl font-bold">Profile & settings</h1>

          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                {user?.profileImage ? (
                  <div
                    className="w-20 h-20 rounded-full bg-center bg-cover bg-no-repeat border-2 border-chalk-blue"
                    style={{ backgroundImage: `url(${user.profileImage})` }}
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-chalk-blue flex items-center justify-center text-blackboard text-3xl font-bold border-2 border-chalk-blue">
                    {profileInitial}
                  </div>
                )}
                <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity">
                  {uploadingPicture ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera size={24} className="text-white" />
                  )}
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePictureChange} className="hidden" />
            </div>
            <div>
              <div className="text-white text-xl font-semibold">{user?.fullName}</div>
              <div className="text-gray-400 text-sm">{user?.email}</div>
              <div className="text-gray-500 text-xs capitalize">{userProfile?.account_type || 'student'}</div>
            </div>
          </div>

          {/* Profile information */}
          <div className="bg-[#111827] rounded-lg border border-gray-600 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <User size={20} className="text-chalk-blue" />
                <h2 className="text-white text-lg font-semibold">Profile information</h2>
              </div>
              <button
                onClick={openEditModal}
                className="flex items-center gap-1.5 text-sm text-chalk-blue hover:text-chalk-blue/80 transition-colors"
              >
                <PencilSimple size={16} />
                Edit
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                <span className="text-gray-400 text-sm">Full name</span>
                <span className="text-white text-sm">{userProfile?.full_name || '—'}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-gray-700/50">
                <span className="text-gray-400 text-sm">Email</span>
                <span className="text-white text-sm">{user?.email || '—'}</span>
              </div>
              {VOICE_MODE_ENABLED && (
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-400 text-sm">Preferred class type</span>
                  <span className="text-white text-sm capitalize">{userProfile?.preferred_class_type || 'text'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Security */}
          <div className="bg-[#111827] rounded-lg border border-gray-600 p-6">
            <div className="flex items-center gap-2 mb-5">
              <Lock size={20} className="text-chalk-blue" />
              <h2 className="text-white text-lg font-semibold">Security</h2>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-gray-400 text-sm mb-1">Password</div>
                <div className="text-white text-sm tracking-widest">••••••••</div>
              </div>
              <button
                onClick={openPasswordModal}
                className="text-sm bg-transparent border border-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Change password
              </button>
            </div>
          </div>

          {/* Subscription — hidden for complimentary pre-existing users */}
          {!isComplimentary && <div className="bg-[#111827] rounded-lg border border-gray-600 p-6">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard size={20} className="text-chalk-blue" />
              <h2 className="text-white text-lg font-semibold">Subscription</h2>
            </div>
            {subscription ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Plan</span>
                  <span className="text-white font-semibold capitalize">{isTrial ? 'Free trial' : tier}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Status</span>
                  <span className={`font-semibold text-sm ${isActive ? 'text-green-400' : 'text-red-400'}`}>
                    {isTrial ? 'Trial' : subscription.status}
                  </span>
                </div>
                {daysLeft !== null && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">{isTrial ? 'Trial ends in' : 'Renews in'}</span>
                    <span className="text-white text-sm">{daysLeft} day{daysLeft === 1 ? '' : 's'}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Active courses</span>
                  <span className="text-white text-sm">{userProfile?.subjects?.length || 0} / {subscription.course_limit}</span>
                </div>
                <div className="pt-2">
                  <button
                    onClick={() => navigate('/settings/subscription')}
                    className="w-full bg-primary text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-primary/90 transition"
                  >
                    Manage subscription
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No subscription found.</p>
            )}
          </div>}

        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-[#111827] rounded-lg border border-gray-600 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white text-lg font-semibold">Edit profile</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Full name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  className="w-full bg-[#1a2332] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-chalk-blue"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  readOnly
                  className="w-full bg-[#1a2332] border border-gray-600 rounded-lg px-3 py-2 text-gray-500 text-sm cursor-not-allowed"
                />
              </div>
              {VOICE_MODE_ENABLED && (
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Preferred class type</label>
                  <select
                    value={editClassType}
                    onChange={e => setEditClassType(e.target.value as 'voice' | 'text')}
                    className="w-full bg-[#1a2332] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-chalk-blue"
                  >
                    <option value="text">Text</option>
                    <option value="voice">Voice</option>
                  </select>
                </div>
              )}
              {profileError && <p className="text-red-400 text-sm">{profileError}</p>}
              <div className="flex gap-3 justify-end pt-1">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="px-5 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {savingProfile ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-[#111827] rounded-lg border border-gray-600 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white text-lg font-semibold">Change password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-5">
              After changing your password you'll be signed out and will need to sign in again.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">New password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={handleNewPasswordChange}
                    placeholder="••••••••"
                    className="w-full bg-[#1a2332] border border-gray-600 rounded-lg px-3 py-2 pr-10 text-white text-sm focus:outline-none focus:border-chalk-blue"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showNewPassword ? <VisibilityIcon className="w-4 h-4" /> : <VisibilityOffIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Requirements checklist */}
              <ul className="text-xs space-y-1 pl-0.5">
                <RequirementItem met={passwordRequirements.minLength} label="At least 8 characters" />
                <RequirementItem met={passwordRequirements.hasNumber} label="At least one number" />
                <RequirementItem met={passwordRequirements.hasSpecialChar} label="At least one special character" />
              </ul>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Confirm new password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    placeholder="••••••••"
                    className="w-full bg-[#1a2332] border border-gray-600 rounded-lg px-3 py-2 pr-10 text-white text-sm focus:outline-none focus:border-chalk-blue"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    {showConfirmPassword ? <VisibilityIcon className="w-4 h-4" /> : <VisibilityOffIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {passwordError && <p className="text-red-400 text-sm">{passwordError}</p>}

              <div className="flex gap-3 justify-end pt-1">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="px-5 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {savingPassword ? 'Updating...' : 'Update password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
